import React, { useState, useMemo } from "react"
import { ScrollView } from "~/ui/primitives"
import { observer } from "mobx-react-lite"
import { useFormik } from "formik"
import styled, { useTheme } from "~/ui/styled"
import { Flex, StartFlex, BetweenFlex, CenterFlex, ColumnFlex, IconFont, Text, Button } from "~components/El"
import { TextField } from "~components/InputField"
import useNavigation from "~hooks/useNavigation"
import useGroupActions from "~hooks/useGroupActions"
import { ChatTypes } from "~proto"
import { useStores } from "~models"
import { AddGroupSchema } from "~schema"
import { globalToast } from "~utils"
import AvatarUpload from "./components/AvatarUpload"
import ContactList from "./components/contact-list"
import { scrollViewFillStyle } from "~/ui/scroll-layout"

const Container = styled(ColumnFlex)`
  flex: 1;
  min-height: 0;
  background-color: ${({ theme }) => theme.colors.blockBackground};
  overflow: hidden;
  position: relative;
`

const Dotted = styled(CenterFlex)`
  width: 4px;
  height: 4px;
  margin-right: 10px;
  border-radius: 2px;
  background-color: #9a3412;
`

const StepDot = styled(CenterFlex)<{ $active: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 15px;
  background-color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.secondary};
`

const StepLine = styled(CenterFlex)<{ $active: boolean }>`
  width: 61px;
  height: 2px;
  background-color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.secondary};
`

const StepFooter = styled(CenterFlex)`
  flex-shrink: 0;
  width: 100%;
  padding: 10px 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.blockBackground};
`

const GroupTypeCard = styled(StartFlex)`
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:active {
    opacity: 0.85;
  }
`

const remarks = [
  "游戏群需要审核通过才可以进行游戏",
  "游戏群创建后需要到上压资金的频道进行关联后才可以进行游戏",
  "需要配置指定群成员为赔付机器人和抢包机器人后才可以进行游戏",
  // "咨询联系官方客服：@Opq"
]

const StepStatus = ({ step }: { step: number }) => {
  const theme = useTheme()

  return (
    <BetweenFlex width="100%" height="auto" padding="18px 36px">
      <StepDot $active={step >= 1}>
        <Text fontSize="16px" color={theme.colors.textLight}>
          1
        </Text>
      </StepDot>
      <StepLine $active={step >= 2} />
      <StepDot $active={step >= 2}>
        <Text fontSize="16px" color={theme.colors.textLight}>
          2
        </Text>
      </StepDot>
      <StepLine $active={step >= 3} />
      <StepDot $active={step >= 3}>
        <Text fontSize="16px" color={theme.colors.textLight}>
          3
        </Text>
      </StepDot>
    </BetweenFlex>
  )
}

const Step1 = ({ theme, setStep, onGroupType }: { theme: any, setStep: any, onGroupType: any }) => {
  const options = [
    {
      title: "普通群",
      content: "日常聊天交流",
      value: ChatTypes.GROUP,
      icon: "cjql",
      iconColor: theme.colors.textLight,
      iconSize: 24,
      iconBg: theme.colors.primary,
    },
    // {
    //   title: "扫雷游戏群",
    //   content: "日常聊天交流 最少需要11人",
    //   value: ChatTypes.GROUP_MINE,
    //   icon: "sl",
    //   iconColor: theme.colors.textLight,
    //   iconSize: 24,
    //   iconBg: theme.colors.warning,
    // },
    {
      title: "龙虎游戏群",
      content: "日常聊天交流",
      value: ChatTypes.GROUP_DRAGON,
      icon: "lhq",
      iconColor: theme.colors.textLight,
      iconSize: 24,
      iconBg: theme.colors.danger,
    },
  ]
  return (
    <ColumnFlex height="auto" flex={1} px="10px">
      <Text fontSize="16px" fontWeight={600} color={theme.colors.text}>
        选择群类型
      </Text>
      {options.map((item, index) => (
        <GroupTypeCard
          key={index}
          width="100%"
          height="auto"
          p="16px"
          bg={theme.colors.background}
          borderRadius="8px"
          mt="16px"
          onClick={() => {
            onGroupType(item.value)
            setStep(2)
          }}
        >
          <CenterFlex width="40px" height="40px" borderRadius="8px" bg={item.iconBg} mr="12px">
            <IconFont name={item.icon} size={item.iconSize} color={item.iconColor} />
          </CenterFlex>
          <ColumnFlex flex={1}>
            <Text color={theme.colors.text} fontWeight={600}>
              {item.title}
            </Text>
            <Text color={theme.colors.infoText}>
              {item.content}
            </Text>
          </ColumnFlex>
        </GroupTypeCard>
      ))}
    </ColumnFlex>
  )
}
const Step2 = ({ members, groupType, onSelect }: { members: any[], groupType: any, onSelect: any }) => {
  return (
    <ColumnFlex flex={1} minHeight={0} width="100%">
      <ContactList groupType={groupType} members={members} onSelect={onSelect} />
    </ColumnFlex>
  )
}
const Step3 = ({ theme, members = [], groupType, values, touched, errors, loading, userStore, setFieldValue, onSubmit }: { theme: any, members: any[], groupType: any, values: any, touched: any, errors: any, loading: any, userStore: any, setFieldValue: any, onSubmit: any }) => {
  const placeholder = useMemo(() => {
    if (groupType === ChatTypes.GROUP_MINE) return "请输入扫雷群名称"
    if (groupType === ChatTypes.GROUP_DRAGON) return "请输入龙虎群名称"
    return "请输入普通群名称"
  }, [groupType])

  const contents = useMemo(() => {
    if (groupType === ChatTypes.GROUP_MINE) return["群类型：扫雷游戏群", `群成员：${members.length} 人`, `创建者：${userStore.currentUser.name}`]
    if (groupType === ChatTypes.GROUP_DRAGON) return ["群类型：龙虎游戏群", `群成员：${members.length} 人`, `创建者：${userStore.currentUser.name}`]
    return ["群类型：普通群", `群成员：${members.length} 人`, `创建者：${userStore.currentUser.name}`]
  }, [groupType])
  return (
    <ColumnFlex height="auto" flex={1} minHeight={0}>
      <ScrollView
      style={scrollViewFillStyle}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          paddingHorizontal: 10,
          paddingBottom: 40, // 给按钮留空间
        }}
      >
        <ColumnFlex px="10px">
          <AvatarUpload
            image={values.image}
            
            onChange={({ uri }) => {
              setFieldValue("image", uri);
            }}
          />
          <TextField
            label="群名称"
            suffix={<Text fontSize={12} color={theme.colors.secondaryText}>{values.title.length}/20</Text>}
            value={values.title}
            placeholder={placeholder}
            required={true}
            onChangeText={(value) => setFieldValue("title", value)}
            error={touched.title && errors.title}
          />
          <Flex
            flexDirection="column"
            width="100%"
            mt="10px"
            padding="10px 12px"
            borderRadius="8px"
            bg={theme.colors.background}
          >
            <Text color={theme.colors.text} fontSize={16} fontWeight={600}>群聊详情</Text>
            <Flex flexDirection="column" width="100%" mt="5px">
              {contents.map((content, index) => (
                <StartFlex key={index}>
                  <Text color={theme.colors.secondaryText} fontSize="12px" lineHeight="18px">
                    {content}
                  </Text>
                </StartFlex>
              ))}
            </Flex>
          </Flex>
          {(groupType === ChatTypes.GROUP_MINE || groupType === ChatTypes.GROUP_DRAGON) && (
            <Flex
              flexDirection="column"
              width="100%"
              mt="10px"
              padding="10px 12px"
              borderRadius="8px"
              bg="#fff7ed"
            >
              <Text color="#9a3412" fontSize={16} fontWeight={600}>游戏群重要通知</Text>
              <Flex flexDirection="column" width="100%" mt="5px">
                {remarks.map((content, index) => (
                  <StartFlex key={index}>
                    <Dotted />
                    <Text color="#9a3412" fontSize="12px" lineHeight="18px">
                      {content}
                    </Text>
                  </StartFlex>
                ))}
              </Flex>
            </Flex>
          )}
          
          <CenterFlex width="100%" mt="10px">
            <Button
              width="100%"
              height="40px"
              borderRadius="8px"
              disabled={loading}
              loading={loading}
              bg={theme.colors.primary}
              onClick={onSubmit}
            >
              <Text fontSize="16px" color={theme.colors.textLight}>创建群聊</Text>
            </Button>
          </CenterFlex>
        </ColumnFlex>
      </ScrollView>
    </ColumnFlex>
    
  )
}
const ComponentMap = {
  1: Step1,
  2: Step2,
  3: Step3,
}
const AddGroupChat: React.FC = () => {
  const theme = useTheme()
  const navigation = useNavigation()
  const {chatStore, userStore } = useStores()
  const { handleCreateChatRoom } = useGroupActions()
  const [groupType, setGroupType] = useState(ChatTypes.GROUP)
  const [step, setStep] = useState(1)
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(false)
  const Component = ComponentMap[step]

  const formik = useFormik({
        initialValues: {
            image: "", // 群聊头像
            title: "" // 群聊名称
        },
        validationSchema: AddGroupSchema,
        onSubmit: (values) => {
          if (loading) return
          setLoading(true)
          const users = members.map((member) => {
            return { user_id: member.user_id, user: userStore.users.get(member.user_id.toString()) }
          })
          users.push({ user_id: userStore.currentUser.id, user: userStore.currentUser })

          const chat = chatStore.newGroupChat({
            type: groupType,
            creator_id: userStore.currentUser.id,
            image: values.image,
            title: values.title || "群聊",
            members: users,
          })

          handleCreateChatRoom({
            type: groupType,
            chat_id: chat.chat_id,
            title: chat.title,
            image: chat.image,
            members: members.map(i => i.user_id),
          }).then(({ res }) => {
            const reconciled = chatStore.reconcileCreatedChat(chat, res)
            setLoading(false)
            navigation.navigate("Chat", { chat_id: reconciled.chat_id })
          }).catch(error => {
            setLoading(false)
            globalToast.show(error?.message || "创建群聊失败")
          })    
        },
  });
  const { values, errors, touched, setFieldValue, handleSubmit } = formik;
  
  const disabled = useMemo(() => {
    // if (groupType === ChatTypes.GROUP) return false
    // if (groupType === ChatTypes.GROUP) return members.length < 3
    // if (groupType === ChatTypes.GROUP_MINE) return members.length < 11
    // if (groupType === ChatTypes.GROUP_DRAGON) return members.length < 4
    return false
  }, [groupType, members.length])

  return (
    <Container>
        <StepStatus step={step} />
        <ColumnFlex height="auto" flex={1} minHeight={0}>
          <Component
            theme={theme}
            values={values}
            errors={errors}
            touched={touched}
            loading={loading}
            setFieldValue={setFieldValue}
            groupType={groupType}
            members={members}
            userStore={userStore}
            setStep={setStep}
            onGroupType={(type) => setGroupType(type)}
            onSelect={(member) => {
              setMembers((prev) => {
                const exists = prev.some(item => item.user_id === member.user_id)
                if (exists) {
                  return prev.filter(item => item.user_id !== member.user_id)
                }
                return [...prev, member]
              })
            }}
            onSubmit={handleSubmit}
          />
        </ColumnFlex>

        {step === 2 ? (
          <StepFooter>
            <Button
              width="100%"
              height="40px"
              borderRadius="8px"
              bg={theme.colors.primary}
              disabled={disabled}
              onClick={() => setStep(3)}
            >
              <Text fontSize="16px" color={theme.colors.textLight}>下一步</Text>
            </Button>
          </StepFooter>
        ) : null}
    </Container>
  )
}
export default observer(AddGroupChat)
